import { invertMap } from '@/sections/insights/queryBuilder/entities/common/invert';
import {
  packStringsForAPI,
  unpackStringsForUI,
} from '@/sections/insights/queryBuilder/entities/common/multiValueHelpers';
import {
  getNotFromSimpleFilter,
  getOperatorFromQueryFilter,
  getOperatorFromSimpleFilter,
  getValueFromQueryFilter,
  getValueFromSimpleFilter,
} from '@/sections/insights/queryBuilder/entities/common/translators';
import type {
  SimpleFilter,
  SimpleFilterDefinition,
} from '@/sections/insights/queryBuilder/entities/types';
import type { QueryFilter } from '@/types';

import { portServiceDisplayNames } from './portServices';

const valueMapInverse = invertMap(portServiceDisplayNames);

/**
 * Reusable field definition for Port service.
 * Translates between API service codes and display-friendly UI values.
 */
export const PortService: SimpleFilterDefinition = {
  apiField: 'service',
  uiField: 'Service',
  inputType: 'dropdown',
  getAvailableValues: () =>
    Promise.resolve(
      Object.values(portServiceDisplayNames).sort((left, right) =>
        left.localeCompare(right)
      )
    ),
  operators: ['is', 'is not', 'is any of', 'is none of', 'contains'],

  translateForUI: (queryFilter: QueryFilter): SimpleFilter => {
    const operator = getOperatorFromQueryFilter(queryFilter);
    const unpacked = unpackStringsForUI(queryFilter.value, operator);

    // Map API codes to display names using helper
    const uiValue = getValueFromQueryFilter(portServiceDisplayNames, {
      ...queryFilter,
      value: unpacked,
    });

    return {
      field: 'Service',
      operator,
      value: uiValue,
    };
  },

  translateForAPI: (simpleFilter: SimpleFilter): QueryFilter => {
    // Map display names to API codes using helper
    const mappedValue = getValueFromSimpleFilter(valueMapInverse, simpleFilter);

    // Then pack for API
    const apiValue = packStringsForAPI(
      mappedValue as string | string[],
      simpleFilter.operator
    );

    return {
      field: 'service',
      operator: getOperatorFromSimpleFilter(simpleFilter),
      value: apiValue,
      not: getNotFromSimpleFilter(simpleFilter),
    };
  },
};
